// Dropdown select — the app's own, not the browser's.
//
// A native <select> renders its list with OS chrome: a system menu on macOS, a
// flat gray list on Windows. It ignores our type scale, radii and surfaces, and
// the open list can't be styled at all. So the list is ours: a portaled Popover
// (never clipped, never affects layout) holding role="option" buttons.
//
// Keyboard behaviour mirrors the native control: arrows open it and walk the
// options, Enter/Space picks, Escape closes (Popover). The options are real
// buttons, so focus moves with the arrows and activation comes for free.

import { KeyboardEvent, memo, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';

import { Popover } from './Popover';
import { IconCheck, IconChevronDown } from './icons';

export interface SelectOption {
  value: string;
  label: string;
}

interface Props {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  /** Accessible name — a button has no <label for>. */
  label?: string;
  disabled?: boolean;
  /** Extra class on the trigger, for width/spacing at the call site. */
  className?: string;
  /**
   * Class on the portaled list. The list leaves the DOM subtree it was written
   * in, so a themed scope (the light admin console) has to travel with it.
   */
  popoverClassName?: string;
}

export const Select = memo(function Select({
  value,
  options,
  onChange,
  label,
  disabled = false,
  className,
  popoverClassName,
}: Props) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const selRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((o) => !o), []);

  const pick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const v = e.currentTarget.dataset.v;
      if (v !== undefined) onChange(v);
      setOpen(false);
      btnRef.current?.focus();
    },
    [onChange],
  );

  const onTriggerKey = useCallback((e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
    }
  }, []);

  // Arrows walk the list; everything else the option buttons handle themselves.
  const onListKey = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="option"]'));
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    const next = e.key === 'ArrowDown' ? i + 1 : i - 1;
    items[Math.min(Math.max(next, 0), items.length - 1)]?.focus();
  }, []);

  // Open on the current value, the way a native select does. preventScroll:
  // the popover is still parked off-screen for its first measuring pass.
  useEffect(() => {
    if (open) selRef.current?.focus({ preventScroll: true });
  }, [open]);

  const current = options.find((o) => o.value === value);

  return (
    <div className={className ? `bz-select-wrap ${className}` : 'bz-select-wrap'}>
      <button
        ref={btnRef}
        type="button"
        className="bz-select"
        onClick={toggle}
        onKeyDown={onTriggerKey}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
      >
        <span className="bz-select-val">{current?.label ?? value}</span>
        <IconChevronDown size={15} className="bz-select-caret" />
      </button>
      {open && (
        <Popover anchor={btnRef.current} onClose={close} matchWidth className={popoverClassName}>
          <div className="bz-select-list" role="listbox" aria-label={label} onKeyDown={onListKey}>
            {options.map((o) => {
              const on = o.value === value;
              return (
                <button
                  key={o.value}
                  ref={on ? selRef : null}
                  type="button"
                  role="option"
                  aria-selected={on}
                  className={on ? 'bz-select-opt bz-select-opt-on' : 'bz-select-opt'}
                  data-v={o.value}
                  onClick={pick}
                >
                  <span>{o.label}</span>
                  {on && <IconCheck size={14} />}
                </button>
              );
            })}
          </div>
        </Popover>
      )}
    </div>
  );
});
